import React, { useState } from 'react';

interface Option {
  id: number;
  value: string;
}

import { useTranslation } from 'react-i18next';
import { Input, Label } from '../AccountProfile/ProfileStyles';
import BiButton from '../primitives/buttons/BiButton.primitive';
import DeleteIcon from '@heroicons/react/outline/TrashIcon';
import CheckCicleIcon from '@heroicons/react/outline/CheckCircleIcon';
import MinusCircleIcon from '@heroicons/react/outline/MinusCircleIcon';
import PlusIcon from '@heroicons/react/outline/PlusIcon';
import { plusSign } from '../../assets/components/svgs';
import { cloneDeep } from 'lodash';
import InfoPopover from '../shared/Fields/InfoPopover';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/outline';
import RadioButton from '../shared/Fields/RadioButton';

interface EnumOptionsProps {
  formData: any;
  setFormData: Function;
}

const EnumOptions: React.FC<EnumOptionsProps> = ({ formData, setFormData }: EnumOptionsProps) => {
  const [newOption, setNewOption] = useState<string>('');
  const [editedOption, setEditedOption] = useState<any>({ index: -1, value: '' });
  const [selectedOption, setSelectedOption] = useState<string>('');
  const [overlayVisible, setOverlayVisible] = useState<boolean>(false);

  const { t } = useTranslation();

  const { enumTypes: options = [], default: defaultValues } = formData;

  const setOptions = (options: Array<string>) => {
    setFormData({ ...formData, enumTypes: options });
  };

  const handleAddOption = () => {
    const trimmedOption = newOption.trim();
    if (trimmedOption !== '' && !options.includes(trimmedOption)) {
      setOptions([...options, trimmedOption]);
      setNewOption('');
    }
  };

  const handleDeleteOption = (value: string) => {
    const cloneOptions = cloneDeep(options);
    const index = options.indexOf(value);
    if (index !== -1) {
      cloneOptions.splice(index, 1);
    }

    setOptions(cloneOptions);
  };

  const handleOptionChange = (index: number, value: string) => {
    const updatedOptions = [...options];
    updatedOptions[index] = value.trim();
    setOptions(updatedOptions);
  };

  const handleOptionBlur = (index: number, value: string) => {
    const trimmedValue = value.trim();
    if (trimmedValue !== options[index] && !options.includes(trimmedValue)) {
      handleOptionChange(index, trimmedValue);
    }
    setEditedOption({ index: -1, value: '' });
  };

  const handleSetAsDefault = (isRemove: boolean) => {
    setFormData({ ...formData, default: isRemove ? '' : selectedOption });
    setOverlayVisible(false);
  };

  const handleRemoveOption = () => {
    handleDeleteOption(selectedOption);
    setSelectedOption('');
    setOverlayVisible(false);
  };

  return (
    <div>
      <Label>{t('T_ENUM_OPTIONS')}</Label>
      <div className="mb-4 flex items-center">
        <Input
          type="text"
          data-testid="enum-addition-input"
          id={`enum-addtion-input`}
          name={t('T_ENUM_ADD_PLACEHOLDER')}
          className="flex-1 rounded border px-3 py-2"
          maxLength={256}
          required={true}
          value={newOption}
          placeholder={t('T_ENUM_ADD_PLACEHOLDER')}
          onChange={(e) => setNewOption(e.target.value)}
        />

        <div
          data-testid="enum-add-button"
          className="ml-2 flex cursor-pointer items-center justify-center text-primary"
          onClick={handleAddOption}
        >
          <div className="mr-2 self-center text-primary">{plusSign}</div> {t('T_ADD_OPTION')}
        </div>
      </div>

      {options.length ? (
        <div>
          <Label>{t('T_ENUM_ADDED_OPTIONS')}</Label>
          <ul className="max-h-48 overflow-y-auto rounded border border-gray-300 p-2">
            {options.map((option: string, index: number) => (
              <li key={index} className="mb-2 flex pt-2">
                <RadioButton
                  selectedField={defaultValues}
                  className="pt-3"
                  id={`${option}-${index}`}
                  option={option}
                  name={option}
                  onClick={() => {
                    setFormData({ ...formData, default: defaultValues === option ? '' : option });
                  }}
                />
                <div className="mr-2 flex-1 flex-col">
                  <Input
                    type="text"
                    data-testid={`enum-edit-input`}
                    id={`enum-edit-input`}
                    className="w-full rounded border"
                    maxLength={256}
                    required={true}
                    value={
                      editedOption.index === index && editedOption.value
                        ? editedOption.value
                        : option
                    }
                    onChange={(e) => setEditedOption({ index, value: e.target.value })}
                    onBlur={(e) => handleOptionBlur(index, e.target.value)}
                  />
                  {option === defaultValues ? (
                    <div className="text-base font-normal">
                      {t('T_REMOVE_DEFAULT_SELECTED_MSG1')}
                    </div>
                  ) : (
                    ''
                  )}
                </div>
                <div className="mr-2 mt-2">
                  {selectedOption === option ? (
                    <ChevronUpIcon
                      className="h-6 w-6 cursor-pointer text-[#127ADB] hover:text-sky-800"
                      onClick={() => setSelectedOption('')}
                    />
                  ) : (
                    <ChevronDownIcon
                      className="h-6 w-6 cursor-pointer text-[#127ADB] hover:text-sky-800"
                      onClick={() => setSelectedOption(option)}
                    />
                  )}
                  {selectedOption === option && (
                    <div className="absolute right-0 z-10 mr-4 mt-2 rounded-md border border-gray-300 bg-white shadow-md">
                      <button
                        className="block w-full px-4 py-2 text-left hover:bg-gray-100"
                        onClick={() => handleSetAsDefault(defaultValues === option)}
                      >
                        {defaultValues === option
                          ? t('T_REMOVE_DEFAULT_SELECTED_MSG2')
                          : t('T_DEFAULT_OPTION_SELECTED')}
                      </button>
                      <button
                        className="block w-full px-4 py-2 text-left hover:bg-gray-100"
                        onClick={handleRemoveOption}
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </div>

                {/* <DeleteIcon
                  data-testid="delete-icon"
                  className="m-auto h-[40px] w-[40px] cursor-pointer p-2 text-[#127ADB] hover:text-sky-800"
                  onClick={() => handleDeleteOption(option)}
                /> */}
              </li>
            ))}
          </ul>
        </div>
      ) : (
        ''
      )}
    </div>
  );
};

export default EnumOptions;
